import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router';
import {
  Search,
  CheckCircle,
  Clock,
  Bug,
  Lightbulb,
  Wrench,
  HelpCircle,
  MessageSquare,
  ArrowUpDown,
} from 'lucide-react';
import {
  FilterChip,
  FilterGroup,
  ClearFiltersButton,
} from '@/components/list/filter-chip';
import { formatRelative } from '@/components/list/format-relative';
import { formatPhone } from '@/lib/format/phone';
import { adminFetch } from '@/api';
import { MarkReviewedDialog } from '@/components/feedback-detail/mark-reviewed-dialog';
import { toast } from '@/components/feedback/toast';
import {
  STATUS_FILTERS,
  decisionMeta,
  statusFilterParams,
  statusLabel,
  type ReviewDecision,
  type StatusFilter,
} from '@/lib/feedback/review-decision';

interface Feedback {
  id: string;
  category: string;
  message: string;
  status: string;
  /** do_now | do_later | wont_do while reviewed. */
  reviewDecision: string | null;
  createdAt: string;
  /** Digits, "+" form for newer apps — see formatPhone. */
  contactPhone: string | null;
  user: { name: string; email: string };
}

type SortKey = 'createdAt' | 'status' | 'category';
type SortDir = 'asc' | 'desc';
type CategoryFilter = 'all' | 'bug' | 'feature' | 'improvement' | 'other';

const CATEGORY_META: Record<
  string,
  { icon: typeof Bug; color: string; bg: string }
> = {
  bug: { icon: Bug, color: 'text-red-400', bg: 'bg-red-500/10' },
  feature: { icon: Lightbulb, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  improvement: { icon: Wrench, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  other: { icon: HelpCircle, color: 'text-zinc-400', bg: 'bg-zinc-500/10' },
};

const SORT_OPTIONS: Array<{ key: SortKey; label: string }> = [
  { key: 'createdAt', label: 'Newest first' },
  { key: 'status', label: 'Status' },
  { key: 'category', label: 'Category' },
];

export default function FeedbackPage() {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [reviewingId, setReviewingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useState<SortKey>('createdAt');
  const [dir, setDir] = useState<SortDir>('desc');

  const fetchFeedback = useCallback(async () => {
    const params = new URLSearchParams({ sort, dir });
    for (const [k, v] of Object.entries(statusFilterParams(status))) params.set(k, v);
    if (category !== 'all') params.set('category', category);
    if (search) params.set('search', search);
    const res = await adminFetch(`/api/feedback?${params.toString()}`);
    const data = await res.json();
    setFeedback(data.feedback);
  }, [status, category, sort, dir, search]);

  useEffect(() => {
    fetchFeedback();
  }, [fetchFeedback]);

  const resolve = async (id: string) => {
    await adminFetch('/api/feedback', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ feedbackId: id, status: 'resolved' }),
    });
    fetchFeedback();
  };

  const markReviewed = async (id: string, picked: ReviewDecision, note: string) => {
    setBusy(true);
    try {
      const res = await adminFetch(`/api/feedback/${id}/status`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status: 'reviewed', decision: picked, message: note }),
      });
      if (!res.ok) {
        toast.error('Status update failed');
        return;
      }
      toast.success(`Reviewed · ${decisionMeta(picked)?.short ?? picked}`);
      setReviewingId(null);
      fetchFeedback();
    } finally {
      setBusy(false);
    }
  };

  const filtersActive =
    status !== 'all' || category !== 'all' || search.length > 0;
  const clearFilters = () => {
    setStatus('all');
    setCategory('all');
    setSearch('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[22px] font-bold text-white tracking-tight">
          Feedback
        </h1>
        <p className="text-zinc-500 text-[13px] mt-0.5">
          {feedback.length.toLocaleString('en-IN')} items
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
          <input
            placeholder="Search by message or user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06] text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-emerald-500/30 transition-all"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.06]">
            <ArrowUpDown className="w-3.5 h-3.5 text-zinc-500" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as SortKey)}
              className="bg-transparent text-[12px] text-white focus:outline-none pr-1"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key} className="bg-[#0b0b0d]">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={() => setDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="px-3 py-2 rounded-xl bg-white/[0.04] border border-white/[0.06] text-[12px] font-semibold text-zinc-300 hover:text-white transition-all"
          >
            {dir === 'asc' ? '↑ Asc' : '↓ Desc'}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        <FilterGroup label="Status">
          {STATUS_FILTERS.map((f) => (
            <FilterChip
              key={f.key}
              label={f.label}
              active={status === f.key}
              onClick={() => setStatus(f.key)}
            />
          ))}
        </FilterGroup>
        <FilterGroup label="Category">
          {(['all', 'bug', 'feature', 'improvement', 'other'] as const).map(
            (v) => (
              <FilterChip
                key={v}
                label={v.charAt(0).toUpperCase() + v.slice(1)}
                active={category === v}
                onClick={() => setCategory(v)}
              />
            ),
          )}
          {filtersActive && <ClearFiltersButton onClick={clearFilters} />}
        </FilterGroup>
      </div>

      <div className="space-y-2">
        {feedback.map((item) => {
          const meta = CATEGORY_META[item.category] || CATEGORY_META.other;
          const Icon = meta.icon;
          const isResolved = item.status === 'resolved';
          const isNew = item.status === 'new';
          const decided = item.status === 'reviewed' ? decisionMeta(item.reviewDecision) : null;
          const statusChipClass = isResolved
            ? 'bg-emerald-500/10 text-emerald-400'
            : decided?.key === 'do_now'
              ? 'bg-red-500/10 text-red-400'
              : decided?.key === 'wont_do'
                ? 'bg-zinc-500/10 text-zinc-400'
                : 'bg-amber-500/10 text-amber-400';

          return (
            <Link
              key={item.id}
              to={`/admin/feedback/${item.id}`}
              className="block bg-white/[0.02] border border-white/[0.06] rounded-2xl p-5 hover:bg-white/[0.03] transition-all"
            >
              <div className="flex items-start gap-4">
                <div
                  className={`w-9 h-9 rounded-xl ${meta.bg} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className={`w-4 h-4 ${meta.color}`} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="text-[13px] font-semibold text-white">
                      {item.user.name}
                    </span>
                    <span
                      className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.bg} ${meta.color}`}
                    >
                      {item.category}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusChipClass}`}
                    >
                      {isResolved ? (
                        <CheckCircle className="w-3 h-3" />
                      ) : (
                        <Clock className="w-3 h-3" />
                      )}
                      {statusLabel(item.status, item.reviewDecision)}
                    </span>
                  </div>
                  <p className="text-[13px] text-zinc-300 leading-relaxed">
                    {item.message}
                  </p>
                  <p className="text-[10px] text-zinc-600 mt-2">
                    {item.user.email}
                    {item.contactPhone ? (
                      <>
                        {' · '}
                        {/* Plain text: the card is itself a link (no nested
                            <a>); the detail page has the tap-to-call link. */}
                        <span className="font-mono text-zinc-400">
                          📞 {formatPhone(item.contactPhone).display}
                        </span>
                      </>
                    ) : null}
                    {' · '}
                    {formatRelative(item.createdAt)}
                  </p>
                </div>

                {!isResolved && (
                  <div className="flex flex-shrink-0 flex-col gap-1.5 sm:flex-row">
                    {isNew && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setReviewingId(item.id);
                        }}
                        className="whitespace-nowrap text-[11px] font-semibold px-3.5 py-1.5 rounded-lg bg-fc-overlay text-fc-text border border-fc-border hover:border-fc-brand transition-all"
                      >
                        Reviewed
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        resolve(item.id);
                      }}
                      className="whitespace-nowrap text-[11px] font-semibold px-3.5 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all"
                    >
                      Resolve
                    </button>
                  </div>
                )}
              </div>
            </Link>
          );
        })}

        {reviewingId && (
          <MarkReviewedDialog
            busy={busy}
            onCancel={() => setReviewingId(null)}
            onSubmit={(picked, note) => markReviewed(reviewingId, picked, note)}
          />
        )}

        {feedback.length === 0 && (
          <div className="text-center py-20 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
            <MessageSquare className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">
              No feedback matches the current filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
